import { QueryIntent } from '../../constants';
import type {
	DetectionMetadata,
	IntentClassificationData,
	PersonIntent,
} from '../../query-intent-api/types';
import type { ElsOverviewData } from './types';

const isPersonIntent = (intentData: IntentClassificationData): intentData is PersonIntent =>
	intentData.classification === QueryIntent.PERSON;

/**
 * Normalizes ELS (Entity Linking Service) overview data for the person-intent
 * debugger tab from the intent-detection classification data and detection
 * metadata.
 *
 * `hasElsData` is true when there is anything meaningful to render for the ELS
 * Overview tab — i.e. prefilter entities, persons, an ELS
 * trace ID, or detection metadata. This drives whether the tab is displayed at
 * all, so the tab only appears when ELS data is present.
 *
 * @param intentData - The intent classification data from the AI Answer context
 * @param detectionMetadata - The intent-detection metadata from the AI Answer context
 * @returns Normalized ELS overview data
 */
export const normalizeElsOverview = (
	intentData: IntentClassificationData | undefined,
	detectionMetadata: DetectionMetadata | undefined,
): ElsOverviewData => {
	const persons =
		intentData && isPersonIntent(intentData) && Array.isArray(intentData.persons)
			? intentData.persons
			: [];

	const prefilterEntities = detectionMetadata?.prefilterEntities ?? [];
	const originalIntent = detectionMetadata?.originalIntent ?? null;
	const elsTraceId = detectionMetadata?.elsTraceId ?? null;

	const hasElsData = prefilterEntities.length > 0 || persons.length > 0 || elsTraceId !== null;

	return {
		hasElsData,
		finalRoutingDecision: intentData?.classification ?? null,
		originalIntent,
		elsTraceId,
		timestamp: detectionMetadata?.timestamp ?? null,
		prefilterEntities,
		persons,
	};
};
